/* eslint-disable */

import { Injectable } from '@angular/core';
import * as moment from 'moment';
import { map } from 'rxjs/operators';
import { DeviceAdapter } from 'src/app/adapters/device/device.adapter';
import { MSRemoteLoggerCategory } from 'src/app/adapters/remote-logger/utils/remote-logger.utils';
import { CONSTANTS } from '../config.service';
import { GisService } from '../gis.service';
import { LoggerService } from '../logger.service';
import { PersonsService } from '../persons.service';
import { PrefsService } from '../prefs.service';
import { StudyService } from '../study.service';
import { TripSurveyService } from '../trip-survey.service';

@Injectable({
  providedIn: 'root'
})
export class MatchersService {
  constructor(
    private logger: LoggerService,
    private study: StudyService,
    private persons: PersonsService,
    private prefs: PrefsService,
    private gis: GisService,
    private tripSurvey: TripSurveyService,
    private deviceAdapter: DeviceAdapter
  ) {}

  isBrowser(surveyData) {
    return CONSTANTS.IS_BROWSERMOVE;
  }

  isDevice(surveyData) {
    return CONSTANTS.IS_DEVICE;
  }

  async isOS(surveyData, os: string): Promise<boolean> {
    const checkOSObs = this.deviceAdapter.getDeviceInfo().pipe(
      map(deviceInfo => {
        const checkOS = deviceInfo.platform === os;
        return checkOS;
      })
    )

    const isOS = await checkOSObs.toPromise();
    return isOS;
  }

  async isBelowOSVersion(surveyData, version: string): Promise<boolean> {
    const checkBelowVersionObs = this.deviceAdapter.getDeviceInfo().pipe(
      map(deviceInfo => {
        const checkBelowVersion = deviceInfo.version < version;
        return checkBelowVersion;
      })
    )

    const isBelowVersion = await checkBelowVersionObs.toPromise();
    return isBelowVersion;
  }

  hasPrefWithValue(surveyData, key, val) {
    return this.prefs
      .getPromise(key)
      .then((res) => res === val)
      .catch(() => false);
  }

  hasIssuanceType(surveyData, type, val, surveyDate?) {
    // types - tripIssuance, dailyIssuance, proxying
    // if we pass in a specific surveyDate, use it (not sure why, but seemed like we could at some point)
    const ymd = surveyDate ? surveyDate : surveyData.surveyDate;
    const phase = this.study.getPhaseByYMD(ymd);
    return phase[type] == val;
  }

  isDayIndexInWeek(surveyData, dayIndices) {
    const dayIndex = moment(surveyData.surveyData, 'YYYYMMDD').day();
    return -1 !== dayIndices.indexOf(dayIndex);
  }

  isDayIndexInStudy(surveyData, dayIndex) {
    const studyDayIndex = this.study.getDayIndexFromYmd(surveyData.surveyDate);
    let dayArr = [];
    // originally, dayIndex was a single numeric value. Would be nice if we could also pass array. Don't want to break existing surveys, so handle both.
    if (typeof dayIndex === 'number') {
      dayArr.push(dayIndex);
    } else {
      dayArr = dayIndex;
    }

    this.logger.log('isDayIndexInStudy', MSRemoteLoggerCategory.SURVEY_MATCHERS_SERVICE, dayArr, studyDayIndex);
    return dayArr.indexOf(studyDayIndex) !== -1;
  }

  //hh functions
  householdSizeGT(ignoredSurveyData, value) {
    return this.persons.getAll().then((persons: any[]) => persons.length > value);
  }

  numParticipantsGT(ignoredSurveyData, value) {
    return this.persons.getParticipants().then((participants: any[]) => participants.length > value);
  }

  // TODO - still using this?
  hasVehicle(ignoredSurveyData) {
    return this.prefs.getPromise('vehicles').then((res: any) => res.length > 0);
  }

  hasVehicleCount(surveyData, value) {
    return this.prefs.getPromiseOrDefault('numVehicles').then((res) => res === value);
  }

  usedOneOwnVehicle(surveyData) {
    return this.prefs.getPromiseOrDefault('numVehicles').then((numVehicles) => {
      if (numVehicles == 0) {
        return false;
      }
      // modes 6-15 are owned vehicles
      // we're relying on the server setting these in order, a little fragile?
      // also in the prepopulate provider for previous surveys
      const start = 6;
      let used = 0;
      for (let veh = start; veh < start + (numVehicles as number); veh++) {
        if (surveyData['_CB_mode_' + veh] === 1) {
          used++;
        }
      }
      return used === 1;
    });
  }

  hasPets() {
    return this.prefs.getPromiseOrDefault('hasPets');
  }

  hasAuthPrefix(ignoredSurveyData, value) {
    return this.prefs
      .getPromise('authCode')
      .then((authCode: string) => value.toUpperCase() === authCode.substring(0, value.length).toUpperCase());
  }

  householdRandomIn(ignoredSurveyData, valueArray, numValues) {
    const rnd = Math.floor(this.persons.getRandom() * numValues);
    return valueArray.some((val) => {
      this.logger.log('householdRandomIn', MSRemoteLoggerCategory.SURVEY_MATCHERS_SERVICE, val, rnd);
      return val === rnd;
    });
  }

  //places functions
  hasPlace(surveyData, placeTypes) {
    let val = false;
    return this.persons.getPersonById(surveyData.subjectPersonId).then((person) => {
      person.places.forEach((p) => {
        const type = p[0];

        if (placeTypes.indexOf(type) !== -1) {
          val = true;
        }
      });
      return val;
    });
  }

  matchesPlace(surveyData, placeTypes, withinMeters) {
    let val = false;
    return this.persons.getPersonById(surveyData.subjectPersonId).then((person) => {
      // for each habitual place, see if it matches
      person.places.forEach((p) => {
        const type = p[0];
        const lat = p[1];
        const lon = p[2];

        if (placeTypes.indexOf(type) !== -1) {
          const distance = this.gis.getDistance(
            this.gis.getFloatLatOrLon(surveyData.stopLat),
            this.gis.getFloatLatOrLon(surveyData.stopLon),
            lat,
            lon
          );
          if (distance < withinMeters) {
            val = true;
          }
        }
      });
      return val;
    });
  }

  matchesOthersPlace(surveyData, placeTypes, withinMeters) {
    let val = false;

    return this.persons.getAll().then((persons: any[]) => {
      persons.forEach((p) => {
        if (p.idx !== surveyData.subjectPersonId) {
          p.places.forEach((p) => {
            const type = p[0];
            const lat = p[1];
            const lon = p[2];

            if (placeTypes.indexOf(type) !== -1) {
              const distance = this.gis.getDistance(
                this.gis.getFloatLatOrLon(surveyData.stopLat),
                this.gis.getFloatLatOrLon(surveyData.stopLon),
                lat,
                lon
              );
              if (distance < withinMeters) {
                val = true;
              }
            }
          });
        }
      });
      return val;
    });
  }

  startAndStopWithinMeters(surveyData, withinMeters) {
    const distance = this.gis.getDistance(
      this.gis.getFloatLatOrLon(surveyData.startLat),
      this.gis.getFloatLatOrLon(surveyData.startLon),
      this.gis.getFloatLatOrLon(surveyData.stopLat),
      this.gis.getFloatLatOrLon(surveyData.stopLon)
    );
    return distance < withinMeters;
  }

  //person functions
  personMeetsCondition(surveyData, key, values, toggleIndex) {
    //toggle index is 0-based but person id's are 1-based. need to add 1.
    //this won't always use a toggle index. If one gets passed, it should be a 'selects' or 'textboxes' question type
    const subjectPersonId = Number.isInteger(toggleIndex) ? toggleIndex + 1 : surveyData.subjectPersonId;

    return this.persons
      .getPersonById(subjectPersonId)
      .then((person) => values.indexOf(person[key]) !== -1)
      .catch(
        (err) =>
          // TODO - does this catch non-people ok?
          null
      );
  }

  isDeviceOwner(surveyData, toggleIndex) {
    //toggle index is 0-based but person id's are 1-based. need to add 1.
    //this won't always use a toggle index. If one gets passed, it should be a 'selects' or 'textboxes' question type
    const subjectPersonId = Number.isInteger(toggleIndex) ? toggleIndex + 1 : surveyData.subjectPersonId;

    return subjectPersonId === this.persons.deviceOwner.idx;
  }

  isParticipant(surveyData, toggleIndex) {
    return true;
  }

  personsMeetCondition(ignoredSurveyData, key, values, matchType) {
    return this.persons.getAll().then((persons: any[]) => {
      const matchCount = persons.filter((p) => values.indexOf(p[key]) !== -1).length;
      let matched = false;
      if (matchType == 'any' && matchCount > 0) {
        matched = true;
      }
      if (matchType == 'all' && matchCount === persons.length) {
        matched = true;
      }

      return matched;
    });
  }

  otherHHTravelersMeetCondition(surveyData, key, values, matchType) {
    // for householders question, the survey subject is always _CB_householders_0;
    return this.persons.getAll().then((persons: any[]) => {
      let i = 1;
      let matchCount = 0;
      let matched = false;

      // if live alone, can't have any or all other members
      if (persons.length === 1) {
        return false;
      }

      persons.forEach((p) => {
        // if on trip, and matches the condition
        if (surveyData['_CB_householders_' + i] && values.indexOf(p[key]) !== -1) {
          matchCount++;
        }
        i++;
      });

      if (matchType == 'any' && matchCount > 0) {
        matched = true;
      }

      if (matchType == 'all' && matchCount === persons.length - 1) {
        matched = true;
      }

      return matched;
    });
  }

  numSelectedCheckboxes(surveyData, field, value) {
    let count = 0;
    Object.keys(surveyData).forEach((key) => {
      if (
        key.substr(0, 4) === '_CB_' &&
        key.substr(4, field.length) === field &&
        key.charAt(field.length + 4) === '_' &&
        surveyData[key] === 1
      ) {
        count++;
      }
    });
    return count === value;
  }

  // //trip functions
  tripNumDivisibleBy(surveyData, value) {
    return this.tripSurvey.countPreviousSurveys(surveyData.startTime).then((res) => res % value === 0);
  }

  // JS - 12/17/18. so far, this has only been used for questions in the daily survey, primarily the "why didn't you make any trips today" question
  // when counting trips, we want to exclude kid trips, so now it will filter by the survey subject, which for daily surveys, is the device owner
  hasTripsThisDay(surveyData) {
    return this.tripSurvey.countSurveysByDayForSurveySubject(surveyData).then((res) => res > 0);
  }

  hasTripsThisDayProxy(surveyData) {
    return Promise.all([
      this.tripSurvey.countSurveysByDayForSurveySubject(surveyData),
      this.tripSurvey.countSurveysByDayForTraveler(surveyData)
    ]).then((res) => res[0] + res[1] > 0);
  }

  hasTripSurveyThisDayWithFieldVal(surveyData, field, values) {
    let matchingSurveys;
    let returnVal = false;
    return this.tripSurvey.getByPersonId(surveyData.subjectPersonId).then((surveys) => {
      matchingSurveys = surveys.filter((s) => s.surveyDate == surveyData.surveyDate && values.includes(s[field]));
      if (matchingSurveys.length) {
        returnVal = true;
      }
      return returnVal;
    });
  }

  //survey functions
  hasSignupSurveyData(surveyData) {
    return this.prefs.getPromiseOrDefault(CONSTANTS.PREF_KEY_SIGNUP_SURVEY_RESPONSES).then((res) => !!res);
  }

  hasSignupSurveyResponse(surveyData, key, values, toggleIndex) {
    return this.prefs.getPromiseOrDefault(CONSTANTS.PREF_KEY_SIGNUP_SURVEY_RESPONSES).then((res) => {
      const vals = [];
      let maxIdx = -1;
      const getMaxIdx = () => {
        for (let i = 0; i < 99; i++) {
          const k = key + '_' + i;
          if (res.hasOwnProperty(k)) {
            maxIdx = i;
          }
        }
      };
      // e.g., if toggle index is 1 and we want to see if fuelType is 4, we check that fuel_type_1 = FUEL_TYPE_1_4
      // the recipe function for a selects question will look like {fn: ["hasSignupSurveyResponse", "fuel_type", [4]]}
      if (toggleIndex != null) {
        getMaxIdx();
        key = key + '_' + toggleIndex;

        // if we've exhausted the number of possible entries, return null to stop the loop
        if (toggleIndex > maxIdx) {
          return null;
        }
      }

      values.forEach((v) => {
        //vals.push(key.toUpperCase() + '_' + v);
        vals.push(v);
      });
      //CHANGING FORMAT OF DATA DOWNLOAD FOR INDRI
      //return  res && res[key] !== undefined && vals.some(x=> res[key].includes(x));
      return res && res[key] !== undefined && vals.indexOf(res[key]) !== -1;
    });
  }

  hasSignupSurveyResponseForIndex(surveyData, key, values, index, idxAdjuster) {
    return this.prefs.getPromiseOrDefault(CONSTANTS.PREF_KEY_SIGNUP_SURVEY_RESPONSES).then((res) => {
      const vals = [];
      const idx = typeof index === 'number' ? index : surveyData[index] + idxAdjuster;

      key = key + '_' + idx;

      values.forEach((v) => {
        //vals.push(key.toUpperCase() + '_' + v);
        vals.push(v);
      });
      //return  res && res[key] !== undefined && vals.some(x=> res[key].includes(x));
      return res && res[key] !== undefined && vals.indexOf(res[key]) !== -1;
    });
  }

  hasPreviousSurvey(surveyData) {
    return this.tripSurvey.getPreviousSurvey(surveyData.startTime, surveyData.subjectPersonId, null).then((res) => !!res);
  }

  isPrepopulate(data) {
    return this.tripSurvey
      .prepopulate(data)
      .then((res) => true)
      .catch(() => false);
  }

  isProxySurvey(surveyData) {
    // it's a proxy survey if the subject of the survey is not the device owner
    return Promise.all([this.persons.getDeviceOwner(), this.persons.getPersonById(surveyData.subjectPersonId)]).then((res) => {
      const kidAgeVals = [3, 4, 1];
      const deviceOwner = res[0];
      const surveySubject = res[1];
      const isUnderEighteen = kidAgeVals.indexOf(surveySubject.age) !== -1;

      // it's a proxy if the device owner is not the survey subject, and
      if (CONSTANTS.IS_BROWSERMOVE) {
        return isUnderEighteen;
      } else {
        return surveyData.subjectPersonId !== deviceOwner.idx;
      }
    });
  }

  hasNonParticipantOnTrip(surveyData) {
    let hasNP = false;
    const prefix = '_CB_householders_';

    return this.persons.getNonParticipants().then((nps) => {
      nps.forEach((np) => {
        if (surveyData[prefix + np.idx] === 1) {
          hasNP = true;
        }
      });
      return hasNP;
    });
  }

  //signup survey specific
  onlyOneProxyChoice(surveyData) {
    // HACK_CB
    let i = 0;
    const age = 'hhAges_';
    const phone = '_CB_hhSmartPhones_';
    let ageGroup;
    let hasPhone;
    let canProxyCount = 0;
    for (; true; i++) {
      ageGroup = surveyData[age + i];
      hasPhone = surveyData[phone + i];
      if (ageGroup === null && hasPhone === null) {
        break;
      } else if (ageGroup === 2 /* 18+ */ && hasPhone === 1 /* w/phone */) {
        canProxyCount++;
      }
    }
    return canProxyCount === 1;
  }

  noHhSurveyParticipants(surveyData) {
    // HACK_CB
    let i = 0;
    const age = 'hhAges_';
    const phone = '_CB_hhSmartPhones_';
    let ageGroup;
    let hasPhone;
    for (; true; i++) {
      ageGroup = surveyData[age + i];
      hasPhone = surveyData[phone + i];
      if (ageGroup === null && hasPhone === null) {
        return true; // past people and no participants
      } else if ((ageGroup === 1 || ageGroup === 2) /* 16+ */ && hasPhone === 1) {
        // 16+ w/phone
        return false;
      }
    }
  }

  neededProxyNotAvailable(surveyData) {
    // HACK_CB
    let i = 0;
    const age = 'hhAges_';
    const phone = '_CB_hhSmartPhones_';
    let ageGroup;
    let hasPhone;
    let hasKids = false;
    let has18Plus = false;
    for (; true; i++) {
      ageGroup = surveyData[age + i];
      hasPhone = surveyData[phone + i];
      if (ageGroup === null && hasPhone === null) {
        break;
      } else if (ageGroup === 0) {
        hasKids = true;
      } else if (hasPhone === 1 && ageGroup === 2) {
        has18Plus = true;
      }
    }
    return hasKids && !has18Plus;
  }

  allHhSurveyParticipants(surveyData) {
    // HACK_CB
    let i = 0;
    const age = 'hhAges_';
    const phone = '_CB_hhSmartPhones_';
    let ageGroup;
    let hasPhone;
    for (; true; i++) {
      ageGroup = surveyData[age + i];
      hasPhone = surveyData[phone + i];
      if (ageGroup === null && hasPhone === null) {
        return true; // past people and all participants
      } else if ((ageGroup === 1 || ageGroup === 2) /* 16+ */ && hasPhone === 1) {
        // 16+ w/phone
        continue;
      } else {
        return false;
      }
    }
  }
}
